import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, Truck, Store, ShoppingCart, Loader2, Gift, ChevronRight, ChevronLeft, Check, Lock, UserCheck, Sparkles, CreditCard, Building2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { id: 1, label: "Panier" },
  { id: 2, label: "Livraison" },
  { id: 3, label: "Paiement" },
];

function Stepper({ step, go }) {
  return (
    <div className="flex items-center justify-center mb-10" data-testid="checkout-stepper">
      {STEPS.map((s, i) => {
        const done = step > s.id;
        const active = step === s.id;
        return (
          <div key={s.id} className="flex items-center">
            <button onClick={() => done && go(s.id)} disabled={!done} data-testid={`step-dot-${s.id}`}
              className={`flex items-center gap-2 ${done ? "cursor-pointer" : "cursor-default"}`}>
              <span className={`flex items-center justify-center h-9 w-9 rounded-full text-sm font-semibold transition-all ${
                active ? "bg-brand text-cream ring-4 ring-brand/20" : done ? "bg-forest text-cream" : "bg-white border border-border text-stone"}`}>
                {done ? <Check size={16} /> : s.id}
              </span>
              <span className={`text-sm font-medium hidden sm:block ${active ? "text-forest" : done ? "text-forest" : "text-stone"}`}>{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`w-10 sm:w-20 h-0.5 mx-2 sm:mx-3 rounded transition-all ${step > s.id ? "bg-forest" : "bg-border"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function Panier() {
  const { items, setQty, remove, add, subtotal } = useCart();
  const { customer } = useCustomerAuth();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("pickup");
  const [address, setAddress] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [customer2, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [settings, setSettings] = useState({ shop_free_delivery_threshold: 300 });
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [discovery, setDiscovery] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [billing, setBilling] = useState({ address: "", postal_code: "", city: "" });
  const [billingSame, setBillingSame] = useState(true);
  const [payMethod, setPayMethod] = useState("cb");
  const canTerms = customer && customer.account_status === "approuve" && customer.payment_terms === "30j_fin_de_mois";

  const checkEmail = async (email) => {
    if (customer !== false || !email || !email.includes("@")) { setEmailExists(false); return; }
    try { const { data } = await api.post("/shop/auth/check-email", { email: email.trim().toLowerCase() }); setEmailExists(!!data.exists); }
    catch { setEmailExists(false); }
  };

  useEffect(() => {
    if (customer) setCustomer({ name: customer.name || "", email: customer.email || "", phone: customer.phone || "" });
  }, [customer]);

  useEffect(() => {
    api.get("/shop/config").then((r) => { setSettings({ shop_free_delivery_threshold: r.data.free_delivery_threshold }); const de = !!r.data.delivery_enabled; setDeliveryEnabled(de); if (!de) setMode("pickup"); }).catch(() => {});
    const ex = items.map((i) => i.product_id).join(",");
    api.get(`/shop/suggested?exclude=${ex}`).then((r) => setSuggested(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const threshold = settings.shop_free_delivery_threshold || 300;
  const remaining = Math.max(0, threshold - subtotal);
  const deliveryFee = mode === "delivery" ? (quote?.fee ?? null) : 0;
  const discount = coupon?.discount || 0;
  const total = useMemo(() => Math.max(0, subtotal - discount) + (deliveryFee || 0), [subtotal, discount, deliveryFee]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try { const { data } = await api.post("/shop/validate-coupon", { code: couponCode, subtotal }); setCoupon(data); toast.success(`Code appliqué : -${data.discount.toFixed(2)} €`); }
    catch (e) { setCoupon(null); toast.error(e?.response?.data?.detail || "Code invalide"); }
  };

  const getQuote = async () => {
    if (!address.trim()) { toast.error("Saisissez une adresse"); return; }
    setQuoting(true);
    try {
      const payload = { address, subtotal, items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) };
      const { data } = await api.post("/shop/quote-delivery", payload);
      setQuote(data);
      if (data.available === false) toast.error(data.note || "Livraison indisponible");
      else if (data.fee === null) toast.error(data.note || "Adresse introuvable");
    } catch { toast.error("Erreur de calcul"); } finally { setQuoting(false); }
  };

  const goToPayment = () => {
    if (!customer2.name || !customer2.email || !customer2.phone) { toast.error("Renseignez vos coordonnées"); return; }
    if (mode === "delivery") {
      if (!address.trim()) { toast.error("Saisissez une adresse de livraison"); return; }
      if (quote?.fee === null || quote?.fee === undefined) { toast.error("Calculez les frais de livraison"); return; }
    }
    const needBilling = !(mode === "delivery" && billingSame);
    if (needBilling && (!billing.address.trim() || !billing.postal_code.trim() || !billing.city.trim())) {
      toast.error("Complétez l'adresse de facturation"); return;
    }
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const billingString = () =>
    (mode === "delivery" && billingSame) ? address : `${billing.address}, ${billing.postal_code} ${billing.city}`.replace(/^,\s*/, "").trim();

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: { ...customer2, billing_address: billingString() }, delivery: { mode, address }, coupon_code: coupon?.code || "", discovery_source: discovery, payment_method: canTerms ? payMethod : "cb", origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur au paiement"); setLoading(false); }
  };

  if (items.length === 0) return (
    <div className="bg-cream min-h-screen pt-40 pb-40 text-center">
      <Seo title="Votre panier" path="/boutique/panier" />
      <ShoppingCart className="mx-auto text-stone/40 mb-4" size={40} />
      <p className="text-stone mb-6">Votre panier est vide.</p>
      <Link to="/boutique" className="rounded-full bg-brand text-cream px-6 py-3 font-semibold hover:bg-forest">Découvrir la boutique</Link>
    </div>
  );

  const Summary = () => (
    <div className="rounded-2xl bg-white border border-border p-6 h-fit lg:sticky lg:top-28" data-testid="checkout-panel">
      <h3 className="font-semibold text-forest mb-4">Récapitulatif</h3>
      <div className="space-y-2 text-sm max-h-48 overflow-auto mb-4 pr-1">
        {items.map((it) => (
          <div key={it.product_id} className="flex justify-between gap-2">
            <span className="text-stone truncate">{it.name} {it.is_volume ? `(${it.quantity} ${it.unit_label})` : `× ${it.quantity}`}</span>
            <span className="text-forest whitespace-nowrap">{(it.price * it.quantity).toFixed(2)} €</span>
          </div>
        ))}
      </div>
      <div className="space-y-2 text-sm border-t border-border pt-4">
        <div className="flex justify-between"><span className="text-stone">Sous-total</span><span className="text-forest">{subtotal.toFixed(2)} €</span></div>
        {discount > 0 && <div className="flex justify-between text-green-600"><span>Réduction ({coupon.code})</span><span>−{discount.toFixed(2)} €</span></div>}
        <div className="flex justify-between"><span className="text-stone">{mode === "pickup" ? "Retrait" : "Livraison"}</span><span className="text-forest">{mode === "pickup" ? "Gratuit" : deliveryFee == null ? "—" : deliveryFee === 0 ? "Offerte" : `${deliveryFee.toFixed(2)} €`}</span></div>
        <div className="flex justify-between font-semibold text-lg pt-2 border-t border-border"><span className="text-forest">Total</span><span className="text-forest" data-testid="cart-total">{total.toFixed(2)} €</span></div>
      </div>
      <div className="mt-4 flex gap-2">
        <input value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Code promo" data-testid="coupon-input" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
        <button onClick={applyCoupon} data-testid="coupon-apply" className="rounded-lg border border-brand text-brand px-4 py-2 text-sm font-semibold hover:bg-brand/5">Appliquer</button>
      </div>
    </div>
  );

  return (
    <div className="bg-cream min-h-screen">
      <Seo title="Votre panier" path="/boutique/panier" />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <Stepper step={step} go={setStep} />

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8">
          <div>
            {/* ===== STEP 1: PANIER ===== */}
            {step === 1 && (
              <div data-testid="step-1">
                {deliveryEnabled && (
                <div className="rounded-xl bg-white border border-border p-4 mb-5" data-testid="free-delivery-bar">
                  {remaining > 0 ? (
                    <p className="text-sm text-forest flex items-center gap-2"><Gift size={16} className="text-terracotta" /> Plus que <strong>{remaining.toFixed(2)} €</strong> pour la livraison offerte !</p>
                  ) : (
                    <p className="text-sm text-green-600 flex items-center gap-2"><Gift size={16} /> 🎉 Livraison offerte débloquée !</p>
                  )}
                  <div className="mt-2 h-2 rounded-full bg-sage overflow-hidden"><div className="h-full bg-brand transition-all" style={{ width: `${Math.min(100, (subtotal / threshold) * 100)}%` }} /></div>
                </div>
                )}

                <div className="rounded-xl bg-white border border-border divide-y divide-border">
                  {items.map((it) => (
                    <div key={it.product_id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${it.slug}`}>
                      {it.image && <img src={fileUrl(it.image)} alt={it.name} className="h-16 w-16 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-forest truncate">{it.name}</p>
                        {it.is_volume ? (
                          <p className="text-sm text-brand">{it.price.toFixed(2)} € / {it.unit_label} · <span className="text-forest font-medium">{it.quantity} {it.unit_label}</span></p>
                        ) : (
                          <p className="text-sm text-brand">{it.price.toFixed(2)} €</p>
                        )}
                      </div>
                      {it.is_volume ? (
                        <span className="rounded-full bg-sage px-3 py-1.5 text-sm font-semibold text-forest whitespace-nowrap" data-testid={`vol-line-${it.slug}`}>{(it.price * it.quantity).toFixed(2)} €</span>
                      ) : (
                        <div className="flex items-center rounded-full border border-border">
                          <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="h-8 w-8 flex items-center justify-center"><Minus size={14} /></button>
                          <span className="w-7 text-center text-sm">{it.quantity}</span>
                          <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="h-8 w-8 flex items-center justify-center"><Plus size={14} /></button>
                        </div>
                      )}
                      <button onClick={() => remove(it.product_id)} data-testid={`remove-${it.slug}`} className="text-red-500 hover:bg-red-50 rounded-lg p-2"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>

                {suggested.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-display text-2xl text-forest mb-4">Complétez votre commande</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="suggested-products">
                      {suggested.map((p) => (
                        <div key={p.id} className="rounded-2xl bg-white border border-border p-3">
                          {p.image && <Link to={`/boutique/${p.slug}`}><img src={fileUrl(p.image)} alt={p.name} className="aspect-square w-full rounded-lg object-cover mb-2" /></Link>}
                          <p className="text-sm font-medium text-forest line-clamp-1">{p.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-sm text-brand font-semibold">{p.price.toFixed(2)} €</span>
                            <button onClick={() => { add(p); toast.success("Ajouté"); }} className="rounded-full bg-brand/10 text-brand h-7 w-7 flex items-center justify-center hover:bg-brand hover:text-cream"><Plus size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <button onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="next-to-delivery"
                    className="rounded-full bg-brand text-cream px-8 py-3.5 font-semibold hover:bg-forest transition-colors flex items-center gap-2">
                    Continuer <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* ===== STEP 2: LIVRAISON & COORDONNÉES ===== */}
            {step === 2 && (
              <div data-testid="step-2" className="space-y-6">
                {customer === false && (
                  <div className="rounded-2xl border border-brand/30 bg-brand/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="login-invite">
                    <p className="text-sm text-forest flex items-center gap-2"><UserCheck size={18} className="text-brand" /> <span><strong>Déjà client ?</strong> Connectez-vous pour retrouver vos adresses, devis et historique.</span></p>
                    <Link to="/compte" data-testid="login-invite-btn" className="shrink-0 rounded-full bg-brand text-cream px-6 py-2.5 text-sm font-semibold hover:bg-forest transition-colors text-center">Se connecter</Link>
                  </div>
                )}
                <div className="rounded-2xl bg-white border border-border p-6">
                  <h3 className="font-semibold text-forest mb-4">Mode de réception</h3>
                  {deliveryEnabled ? (
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setMode("pickup")} data-testid="mode-pickup" className={`rounded-xl border p-4 text-sm flex flex-col items-center gap-1.5 ${mode === "pickup" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}><Store size={20} /> Retrait à Saint-Victoret</button>
                      <button onClick={() => setMode("delivery")} data-testid="mode-delivery" className={`rounded-xl border p-4 text-sm flex flex-col items-center gap-1.5 ${mode === "delivery" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}><Truck size={20} /> Livraison</button>
                    </div>
                  ) : (
                    <div data-testid="pickup-only">
                      <div className="rounded-xl border border-brand bg-brand/5 text-brand p-4 text-sm flex items-center gap-3" data-testid="mode-pickup">
                        <Store size={22} className="shrink-0" />
                        <div>
                          <p className="font-semibold">Retrait à Saint-Victoret (13730)</p>
                          <p className="text-xs text-stone mt-0.5">665 Avenue du Raumartin · Vous serez contacté pour convenir d'un créneau de retrait.</p>
                        </div>
                      </div>
                      <p className="text-xs text-stone mt-3">🚚 La livraison à domicile sera proposée prochainement.</p>
                    </div>
                  )}
                  {mode === "delivery" && (
                    <div className="mt-4">
                      <input value={address} onChange={(e) => { setAddress(e.target.value); setQuote(null); }} placeholder="Adresse de livraison complète" data-testid="delivery-address" className="w-full rounded-lg border border-border px-3 py-2.5 text-sm mb-2" />
                      <button onClick={getQuote} disabled={quoting} data-testid="quote-btn" className="w-full rounded-lg border border-brand text-brand py-2.5 text-sm font-semibold hover:bg-brand/5 flex items-center justify-center gap-2">{quoting ? <Loader2 className="animate-spin" size={15} /> : <Truck size={15} />} Calculer les frais de livraison</button>
                      {quote?.available === false && <p className="text-xs text-red-500 mt-2" data-testid="quote-unavailable">{quote.note}</p>}
                      {quote?.available !== false && quote?.fee != null && (
                        <div className="mt-2 rounded-lg bg-[#F0EFEA] p-3 text-xs text-stone" data-testid="quote-result">
                          <p className="text-forest font-medium">{quote.free ? "🎉 Livraison offerte" : `Frais de livraison : ${quote.fee.toFixed(2)} €`}</p>
                          <p>Depuis le dépôt de <strong>{quote.depot}</strong> · ~{quote.distance_km} km (aller)</p>
                          {!quote.free && quote.breakdown && <p className="mt-1 opacity-80">Carburant {quote.breakdown.fuel} € · véhicule {quote.breakdown.vehicle} € · main d'œuvre {quote.breakdown.driver} €</p>}
                          <p className="mt-1 italic">Date de livraison fixée par notre service client.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl bg-white border border-border p-6">
                  <h3 className="font-semibold text-forest mb-4">Vos coordonnées</h3>
                  {customer === false && (
                    <Link to="/compte" className="mb-4 block rounded-lg bg-sage text-forest text-xs text-center py-2.5 hover:bg-brand/10" data-testid="save-cart-cta">
                      💚 Créez un compte pour sauvegarder votre panier et le retrouver partout
                    </Link>
                  )}
                  <div className="space-y-2.5">
                    <input value={customer2.name} onChange={(e) => setCustomer({ ...customer2, name: e.target.value })} placeholder="Nom & prénom *" data-testid="cust-name" className="w-full rounded-lg border border-border px-3 py-2.5 text-sm" />
                    <input value={customer2.email} onChange={(e) => { setCustomer({ ...customer2, email: e.target.value }); setEmailExists(false); }} onBlur={(e) => checkEmail(e.target.value)} placeholder="Email *" data-testid="cust-email" className="w-full rounded-lg border border-border px-3 py-2.5 text-sm" />
                    {emailExists && (
                      <div className="rounded-lg bg-brand/10 border border-brand/30 p-3 flex items-start gap-2.5" data-testid="existing-account-banner">
                        <UserCheck size={18} className="text-brand mt-0.5 shrink-0" />
                        <p className="text-xs text-forest">Un compte existe déjà avec cet email. <Link to="/compte" className="font-semibold text-brand underline">Reconnectez-vous</Link> pour retrouver votre historique, vos devis et votre panier sauvegardé.</p>
                      </div>
                    )}
                    <input value={customer2.phone} onChange={(e) => setCustomer({ ...customer2, phone: e.target.value })} placeholder="Téléphone *" data-testid="cust-phone" className="w-full rounded-lg border border-border px-3 py-2.5 text-sm" />
                  </div>

                  <div className="mt-5 pt-5 border-t border-border">
                    <label className="text-sm font-medium text-forest flex items-center gap-2 mb-2"><Sparkles size={15} className="text-brand" /> Comment nous avez-vous connu ?</label>
                    <div className="grid grid-cols-2 gap-2" data-testid="discovery-survey">
                      {[
                        { v: "Google", l: "🔍 Google" },
                        { v: "Bouche-à-oreille", l: "👥 Un proche" },
                        { v: "Réseaux sociaux", l: "📱 Réseaux sociaux" },
                        { v: "Déjà client", l: "💚 Déjà client" },
                        { v: "Publicité", l: "📰 Publicité" },
                        { v: "Autre", l: "✨ Autre" },
                      ].map((o) => (
                        <button key={o.v} type="button" onClick={() => setDiscovery(o.v)} data-testid={`discovery-${o.v}`}
                          className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${discovery === o.v ? "border-brand bg-brand/5 text-brand" : "border-border text-stone hover:border-brand/40"}`}>{o.l}</button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-white border border-border p-6">
                  <h3 className="font-semibold text-forest mb-4">Adresse de facturation</h3>
                  {mode === "delivery" && (
                    <label className="flex items-center gap-2.5 mb-4 cursor-pointer" data-testid="billing-same-label">
                      <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} data-testid="billing-same" className="h-4 w-4 rounded border-border accent-[#2E9E2E]" />
                      <span className="text-sm text-forest">Identique à l'adresse de livraison</span>
                    </label>
                  )}
                  {!(mode === "delivery" && billingSame) && (
                    <div className="space-y-2.5" data-testid="billing-fields">
                      <input value={billing.address} onChange={(e) => setBilling({ ...billing, address: e.target.value })} placeholder="Adresse (n° et rue) *" data-testid="billing-address" className="w-full rounded-lg border border-border px-3 py-2.5 text-sm" />
                      <div className="grid grid-cols-3 gap-2.5">
                        <input value={billing.postal_code} onChange={(e) => setBilling({ ...billing, postal_code: e.target.value })} placeholder="Code postal *" data-testid="billing-postal" className="rounded-lg border border-border px-3 py-2.5 text-sm" />
                        <input value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} placeholder="Ville *" data-testid="billing-city" className="col-span-2 rounded-lg border border-border px-3 py-2.5 text-sm" />
                      </div>
                    </div>
                  )}
                  {mode === "delivery" && billingSame && <p className="text-xs text-stone" data-testid="billing-same-note">La facture sera émise à l'adresse de livraison saisie ci-dessus.</p>}
                </div>

                <div className="flex justify-between">
                  <button onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="back-to-cart" className="rounded-full border border-border text-stone px-6 py-3 font-medium hover:border-brand flex items-center gap-2"><ChevronLeft size={18} /> Retour</button>
                  <button onClick={goToPayment} data-testid="next-to-payment" className="rounded-full bg-brand text-cream px-8 py-3.5 font-semibold hover:bg-forest transition-colors flex items-center gap-2">Continuer <ChevronRight size={18} /></button>
                </div>
              </div>
            )}

            {/* ===== STEP 3: PAIEMENT ===== */}
            {step === 3 && (
              <div data-testid="step-3" className="space-y-6">
                <div className="rounded-2xl bg-white border border-border p-6">
                  <h3 className="font-semibold text-forest mb-4">Vérifiez votre commande</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-stone">Réception</span><span className="text-forest font-medium">{mode === "pickup" ? "Retrait à Saint-Victoret" : "Livraison"}</span></div>
                    {mode === "delivery" && <div className="flex justify-between gap-4"><span className="text-stone">Adresse</span><span className="text-forest text-right">{address}</span></div>}
                    <div className="flex justify-between gap-4"><span className="text-stone">Facturation</span><span className="text-forest text-right">{billingString() || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-stone">Nom</span><span className="text-forest">{customer2.name}</span></div>
                    <div className="flex justify-between"><span className="text-stone">Email</span><span className="text-forest">{customer2.email}</span></div>
                    <div className="flex justify-between"><span className="text-stone">Téléphone</span><span className="text-forest">{customer2.phone}</span></div>
                  </div>
                </div>

                <div className="rounded-2xl bg-sage/40 border border-border p-5 flex items-start gap-3">
                  <Lock size={18} className="text-brand mt-0.5 shrink-0" />
                  <p className="text-sm text-forest">Vous allez être redirigé vers notre page de paiement <strong>sécurisée Stripe</strong>. Aucune donnée bancaire n'est stockée sur notre site.</p>
                </div>

                {canTerms && (
                  <div className="rounded-2xl bg-white border border-border p-5" data-testid="payment-method">
                    <h3 className="font-semibold text-forest mb-3">Mode de paiement</h3>
                    <div className="space-y-2">
                      <button onClick={() => setPayMethod("cb")} data-testid="pay-cb" className={`w-full flex items-center gap-3 rounded-xl border p-3 text-sm ${payMethod === "cb" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}><CreditCard size={18} /> Carte bancaire (paiement immédiat)</button>
                      <button onClick={() => setPayMethod("terms")} data-testid="pay-terms" className={`w-full flex items-center gap-3 rounded-xl border p-3 text-sm ${payMethod === "terms" ? "border-brand bg-brand/5 text-brand" : "border-border text-stone"}`}><Building2 size={18} /> Paiement à 30j fin de mois <span className="ml-auto text-xs text-stone">Encours dispo : {Math.max(0, (customer.credit_limit || 0) - (customer.outstanding || 0)).toFixed(0)} € HT</span></button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3">
                  <button onClick={() => { setStep(2); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="back-to-delivery" className="rounded-full border border-border text-stone px-6 py-3 font-medium hover:border-brand flex items-center justify-center gap-2"><ChevronLeft size={18} /> Retour</button>
                  <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
                    className="flex-1 sm:flex-none rounded-full bg-brand text-cream px-8 py-3.5 font-semibold hover:bg-forest transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />} {canTerms && payMethod === "terms" ? `Valider la commande ${total.toFixed(2)} €` : `Payer ${total.toFixed(2)} €`}
                  </button>
                </div>
                <p className="text-[11px] text-stone text-center">Paiement sécurisé par Stripe · sans création de compte</p>
              </div>
            )}
          </div>

          {/* Persistent summary */}
          <Summary />
        </div>
      </div>
    </div>
  );
}
